import { Select } from "@threadws/jute";
import { useState } from "react";
import type { RepeatUnit, TaskRecurrence } from "../../api/generated";
import { repeatLabel } from "../../utils/taskLabels";

const NEVER = "never";
const CUSTOM = "custom";
const UNITS: RepeatUnit[] = ["day", "week", "month", "year"];
const CHOICES = [
  { value: NEVER, label: "Doesn't repeat" },
  ...UNITS.map((unit) => ({ value: unit, label: repeatLabel({ every: 1, unit }) })),
  { value: CUSTOM, label: "Custom…" },
];
const fieldClass =
  "rounded-md border border-border-subtle bg-transparent px-2.5 py-1.5 font-body text-sm text-text-primary focus:border-border-strong focus:outline-none";

/** "3" → 3. Null unless a whole number from 1 to 365. */
const parseEvery = (text: string) => (/^\d+$/.test(text.trim()) && +text >= 1 && +text <= 365 ? +text : null);

interface RepeatSelectProps {
  value: TaskRecurrence | null;
  /** Called with the new repeat (null to stop repeating) as soon as it changes; the count on blur. */
  onChange: (recurrence: TaskRecurrence | null) => void;
}

/** How often a task repeats: a common choice, or Custom… for "every n days, weeks, months or years". */
export function RepeatSelect({ value, onChange }: RepeatSelectProps) {
  const [custom, setCustom] = useState(value !== null && value.every !== 1);
  const [every, setEvery] = useState(String(value?.every === 1 ? 2 : (value?.every ?? 2)));
  const count = parseEvery(every);
  const unit = value?.unit ?? "day";

  const choose = (choice: string) => {
    setCustom(choice === CUSTOM);
    if (choice === NEVER) onChange(null);
    else if (choice === CUSTOM) onChange({ every: count ?? 2, unit });
    else onChange({ every: 1, unit: choice as RepeatUnit });
  };
  const saveEvery = () => {
    if (count === null) setEvery(String(value?.every ?? 2));
    else if (count !== value?.every) onChange({ every: count, unit });
  };

  return (
    <div className="flex flex-col gap-2">
      <Select
        label="Repeats"
        value={custom ? CUSTOM : value === null ? NEVER : value.unit}
        options={CHOICES}
        onChange={(event) => choose(event.target.value)}
      />
      {custom && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          Every
          <input
            type="number"
            min={1}
            max={365}
            aria-label="Repeat every"
            className={`${fieldClass} w-20 ${count === null ? "border-danger" : ""}`}
            value={every}
            onChange={(event) => setEvery(event.target.value)}
            onBlur={saveEvery}
          />
          <select
            aria-label="Repeat unit"
            className={`${fieldClass} flex-1`}
            value={unit}
            onChange={(event) => onChange({ every: count ?? value?.every ?? 2, unit: event.target.value as RepeatUnit })}
          >
            {UNITS.map((option) => (
              <option key={option} value={option}>
                {count === 1 ? option : `${option}s`}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
